/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Stores/SnippetFormStore" />
import * as React from "react";

import { UnparentedInputsGroupName } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import { unsupportedInputs } from "Build/DesignerExtensions/YamlSnippetForm/Components/SnippetInput";
import { ISnippetInputValueProvider } from "Build/DesignerExtensions/YamlSnippetForm/Components/SnippetInputGroup";
import { VisibilityHelper } from "Build/DesignerExtensions/YamlSnippetForm/Utilities/VisibilityHelper";
import { Store } from "Build/Flux/Store";
import { TaskGroupDefinition } from "DistributedTask/Client/Contracts/TaskAgent";
import { YamlSnippet, YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import { ObservableValue } from "VSS/Core/Observable";
import { IAccordionSection } from "VSSUI/Accordion";

export class SnippetFormStore extends Store implements ISnippetInputValueProvider {
    private _snippet: YamlSnippet;
    private _inputsByGroup: { [key: string]: YamlSnippetInput[] } = {};
    private _groupCollapsedState: { [key: string]: ObservableValue<boolean> } = {};
    private _initiallyExpandedGroupsEvaluated: boolean = false;
    private _inputsByName: { [key: string]: YamlSnippetInput } = {};
    private _inputValues: { [key: string]: string } = {};
    private _inputValueAliases: { [key: string]: string } = {};
    private _inputVisibility: { [key: string]: boolean } = {};
    private _groupVisibility: { [key: string]: boolean } = {};
    private _unsupportedInputs: string[];

    public loadSnippet(snippet: YamlSnippet, startingParameters?: { [key: string]: string }): void {
        this._snippet = snippet;
        this._inputsByGroup = {};
        this._inputsByName = {};

        this._organizeInputs();
        if (startingParameters) {
            this._loadStartingParameters(startingParameters);
        }
        this._updateVisibilityMaps();
        this._findInitiallyExpandedGroups();

        this._unsupportedInputs = unsupportedInputs(Object.keys(this._inputsByName).map(name => this._inputsByName[name]));

        this.emitChanged();
    }

    public getValuesForExport = (): { [key: string]: string } => {
        let values: { [key: string]: string } = {};

        // Start by gathering any default values referenced by instanceNameFormat.
        // These seem to be the 'key' inputs and so we include them even if the user didn't modify them
        // We will also export any visible, required inputs that have a default value
        this._snippet.inputs.forEach(i => {
            if (this._snippet.includeInputs.some(input => input === i.name)) {
                values[i.name] = i.defaultValue;
            } else if (
                i.required &&
                this._inputVisibility[i.name] &&
                i.defaultValue &&
                (i.groupName === UnparentedInputsGroupName || this._groupVisibility[i.groupName])
            ) {
                values[i.name] = i.defaultValue;
            }
        });

        // set/overwrite with any values set by the user, preferring value aliases (such as a service connection's name rather than its guid)
        Object.keys(this._inputValues).forEach(inputName => {
            if (
                this._inputValueAliases[inputName] !== undefined &&
                this._inputValueAliases[inputName] !== this._inputsByName[inputName].defaultValue
            ) {
                values[inputName] = this._inputValueAliases[inputName];
            } else if (this._inputValues[inputName] !== undefined && this._inputValues[inputName] !== this._inputsByName[inputName].defaultValue) {
                values[inputName] = this._inputValues[inputName];
            }
        });

        // filter out invisible inputs
        Object.keys(this._inputVisibility).forEach(k => {
            if (!this._inputVisibility[k]) {
                delete values[k];
            }
        });

        return values;
    };

    public setValue = (inputName: string, value: string, valueAlias?: string): void => {
        this._inputValues[inputName] = value;
        if (valueAlias) {
            this._inputValueAliases[inputName] = valueAlias;
        }

        this._updateVisibilityMaps();
        this.emitChanged();
    };

    public getValue = (inputName: string): string => {
        // If the input is not visible, anything referencing this input should see it's value as blank
        if (!this._inputVisibility[inputName]) {
            return "";
        }

        // Return the string value we have set for the input. Empty string is valid
        // If no value is defined, return the default value defined by the snippet
        if (this._inputValues[inputName] !== undefined) {
            return this._inputValues[inputName];
        }
        return (this._inputsByName[inputName] && this._inputsByName[inputName].defaultValue) || "";
    };

    public isVisible = (inputName: string): boolean => {
        return this._inputVisibility[inputName];
    };

    public isGroupVisible = (groupName: string): boolean => {
        return this._groupVisibility[groupName];
    };

    public get visibleGroups(): TaskGroupDefinition[] {
        return this._snippet.groups.filter(groupDefinition => {
            return (
                this._groupVisibility[groupDefinition.name] &&
                this._inputsByGroup[groupDefinition.name] &&
                this._inputsByGroup[groupDefinition.name].length > 0
            );
        });
    }

    public get snippet(): YamlSnippet {
        return this._snippet;
    }

    public get inputsByGroup(): { [key: string]: YamlSnippetInput[] } {
        return this._inputsByGroup;
    }

    public isGroupCollapsed = (groupName: string): ObservableValue<boolean> => {
        return this._groupCollapsedState[groupName];
    };

    public toggleGroup = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, section: IAccordionSection) => {
        const currentValue = this._groupCollapsedState[section.key].value;
        this._groupCollapsedState[section.key].value = !currentValue;
    };

    public toggleGroupByKey = (sectionKey: string) => {
        const currentValue = this._groupCollapsedState[sectionKey].value;
        this._groupCollapsedState[sectionKey].value = !currentValue;
    };

    public get isSnippetSupported(): boolean {
        return this._unsupportedInputs.length === 0;
    }

    public get unsupporedInputTypes(): string[] {
        return this._unsupportedInputs;
    }

    // for debugging only
    public hasInputType(typeString: string): boolean {
        let hasMatch = false;
        let propName: string | undefined;
        if (typeString.indexOf("prop:") >= 0) {
            const split = typeString.split(" ");
            propName = split.find(s => s.indexOf("prop:") >= 0)!.substr(5);
            typeString = typeString.replace("prop:" + propName, "").trim();
        }

        Object.keys(this._inputsByName).forEach(name => {
            const input = this._inputsByName[name];
            if (input.type === typeString) {
                if (propName) {
                    if (input.properties && input.properties[propName] && input.properties[propName].toLowerCase() === "true") {
                        hasMatch = true;
                    }
                } else {
                    hasMatch = true;
                }
            }
        });

        return hasMatch;
    }

    public getInputType = (name: string): string => {
        const typeString = this._inputsByName[name].type;
        if (typeString.indexOf(":") >= 0) {
            return typeString.split(":").pop()!;
        } else {
            return typeString;
        }
    }

    private _organizeInputs(): void {
        this._snippet.inputs.forEach((inputDefinition: YamlSnippetInput) => {
            if (!this._inputsByGroup[inputDefinition.groupName]) {
                this._inputsByGroup[inputDefinition.groupName] = [];
            }

            this._inputsByGroup[inputDefinition.groupName].push(inputDefinition);
            this._inputsByName[inputDefinition.name] = inputDefinition;
        });
    }

    public setInputVisibility = (inputName: string, value: boolean): void => {
        if (this._inputVisibility[inputName] ==! value){
            this._inputVisibility[inputName] = value;
            this.emitChanged();
        }
    };

    private _updateVisibilityMaps() {
        this._snippet.inputs.forEach((inputDefinition: YamlSnippetInput) => {
            this._inputVisibility[inputDefinition.name] = VisibilityHelper.getVisibility(inputDefinition.visibleRule, this.getValue);
        });

        this._snippet.groups.forEach((groupDefinition: TaskGroupDefinition) => {
            const inputs = this._inputsByGroup[groupDefinition.name] || [];
            const visibleInputs = inputs.filter(i => this._inputVisibility[i.name]);
            if (visibleInputs.length === 0) {
                this._groupVisibility[groupDefinition.name] = false;
            } else {
                this._groupVisibility[groupDefinition.name] = VisibilityHelper.getVisibility(groupDefinition.visibleRule, this.getValue);
                // if the group has visible inputs but is not visible, flag all those inputs as not visible
                if (!this._groupVisibility[groupDefinition.name]) {
                    inputs.forEach(input => {
                        this._inputVisibility[input.name] = false;
                    });
                }
            }
        });
    }

    private _findInitiallyExpandedGroups() {
        if (!this._initiallyExpandedGroupsEvaluated){
            this._snippet.groups.forEach((groupDefinition: TaskGroupDefinition) => {
                let isCollapsed = false;

                if (groupDefinition.isExpanded === false) {
                    isCollapsed = true;
                } else {
                    const trimmedGroupDisplayName: string = groupDefinition.displayName.trim().toLowerCase();
                    isCollapsed = trimmedGroupDisplayName.indexOf("advanced") >= 0;
                }

                this._groupCollapsedState[groupDefinition.name] = new ObservableValue<boolean>(isCollapsed);
            });
            this._initiallyExpandedGroupsEvaluated = true;
        }
    }

    private _loadStartingParameters(startingParameters: { [key: string]: string }) {
        Object.keys(startingParameters).forEach(p => {
            let snippetInput: YamlSnippetInput | undefined = undefined;
            if (this._inputsByName[p]) {
                snippetInput = this._inputsByName[p];
            } else {
                // input was written with an alias, find the real parameter name and use that instead
                this._snippet.inputs.forEach(input => {
                    if (input.aliases.indexOf(p) >= 0) {
                        snippetInput = input;
                    }
                });
            }

            const value = startingParameters[p];
            if (snippetInput && value !== null && value !== undefined) {
                this._inputValues[snippetInput.name] = value.toString();
            }
        });
    }
}
